// src/features/academic/components/UserScore.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { Award, Trophy, Flame, User, CheckCircle } from 'lucide-react';

/**
 * User Score Component
 * Displays the user's current score and ranking information
 */
const UserScore = ({ 
  todayScore, 
  totalScore, 
  rank, 
  hasCompletedToday,
  userName,
  userAvatar,
  streakCount = 0,
  university = null
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-xl shadow-md overflow-hidden"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500 to-teal-500 text-white p-4">
        <h2 className="font-bold text-lg flex items-center">
          <Trophy className="mr-2" size={20} />
          Your Challenge Stats
        </h2>
      </div>
      
      {/* User Info */}
      <div className="p-4 flex items-center border-b border-gray-100">
        <div className="w-12 h-12 rounded-full overflow-hidden mr-3 bg-gray-100">
          {userAvatar ? (
            <img 
              src={userAvatar} 
              alt={userName || 'User'} 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-500">
              <User size={24} />
            </div>
          )}
        </div>
        
        <div className="flex-1">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-medium text-gray-800">{userName || 'Guest User'}</h3>
              <div className="flex items-center text-sm text-gray-500">
                <Trophy size={14} className="mr-1 text-amber-500" />
                {rank ? `Rank #${rank}` : 'Not Ranked Yet'}
              </div>
            </div>
            
            {university && (
              <div className="text-right">
                <div className="text-xs text-gray-500">University</div>
                <div className="text-sm font-medium text-gray-700">{university}</div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Score Display */}
      <div className="p-4">
        <div className="grid grid-cols-3 gap-4">
          <ScoreCard 
            title="Today" 
            value={hasCompletedToday ? todayScore : 'Not Taken'} 
            icon={<Award className="text-teal-500" size={18} />} 
            highlight={hasCompletedToday}
          />
          
          <ScoreCard 
            title="Total" 
            value={totalScore || 0} 
            suffix="pts"
            icon={<Trophy className="text-amber-500" size={18} />} 
          />
          
          <ScoreCard 
            title="Streak" 
            value={streakCount || 0} 
            suffix="days"
            icon={<Flame className="text-orange-500" size={18} />} 
          />
        </div>
        
        {/* Status Message */}
        <div className="mt-4 text-center text-sm">
          {hasCompletedToday ? (
            <div className="text-green-600 font-medium px-3 py-1.5 bg-green-50 rounded-lg inline-flex items-center">
              <CheckCircle size={16} className="mr-1.5" />
              You've completed today's challenge!
            </div>
          ) : (
            <div className="text-blue-600">
              Take today's challenge to earn points
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Helper Score Card Component
const ScoreCard = ({ title, value, suffix = '', icon, highlight = false }) => {
  return (
    <div className={`p-3 rounded-lg ${
      highlight 
        ? 'bg-green-50 border border-green-100' 
        : 'bg-gray-50 border border-gray-100'
    }`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-gray-500 font-medium">{title}</span>
        {icon}
      </div>
      <div className={`font-bold text-lg ${
        highlight 
          ? 'text-green-600'
          : typeof value === 'number' && value > 0
            ? 'text-gray-700'
            : 'text-gray-500'
      }`}>
        {value} {suffix && <span className="text-xs font-medium">{suffix}</span>}
      </div>
    </div>
  );
};

export default UserScore;